import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useAlerts } from '../../contexts/AlertContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineArrowLeft,
  HiOutlinePencil,
  HiOutlineFolder,
  HiOutlineCalendarDays,
  HiOutlineCheckCircle,
} from 'react-icons/hi2'
import {
  Banner,
  Button,
  Card,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '../../components/ui'

export default function EditCasePage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const { isWorkspaceSwitched, ownerAccountId } = useAuth()
  const { showSuccess, showError } = useAlerts()
  const { getEntity } = useWorkspace()

  const [formData, setFormData] = useState({ title: '', description: '', client: '', date: '' })
  const [originalData, setOriginalData] = useState({})
  const [clients, setClients] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)
  const [clientsLoading, setClientsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
        // page=all: list endpoints return 10 items per page by default, which left
        // older entries out of this dropdown.
        const response = await apiService.get('/clients/', { params: { ...params, page: 'all' } })
        if (!cancelled && response.success) {
          const data = response.data.data || response.data
          setClients(data.data || data.results || [])
        }
      } catch { /* swallow */ } finally {
        if (!cancelled) setClientsLoading(false)
      }
      try {
        setPageLoading(true)
        const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
        const response = await apiService.get(`/cases/${id}/`, { params })
        if (cancelled) return
        if (response.success) {
          const caseData = response.data.data || response.data
          const data = {
            title: caseData.title || '',
            description: caseData.description || '',
            client: String(caseData.client_id || caseData.client?.id || caseData.client || ''),
            date: caseData.date || '',
          }
          setFormData(data); setOriginalData(data)
        } else {
          showError(response.error?.message || t('cases.loadFailed', { entity: getEntity('case').singular.toLowerCase() }))
          navigate('/cases')
        }
      } catch {
        if (!cancelled) {
          showError(t('cases.loadFailed', { entity: getEntity('case').singular.toLowerCase() }))
          navigate('/cases')
        }
      } finally {
        if (!cancelled) setPageLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, ownerAccountId])

  const validate = () => {
    const next = {}
    if (!formData.title.trim()) next.title = t('cases.create.validationTitleRequired', { entity: getEntity('case').singular })
    if (!formData.client) next.client = t('cases.create.validationClientRequired', { client: getEntity('client').singular.toLowerCase() })
    if (!formData.date) next.date = t('cases.create.validationDateRequired', { entity: getEntity('case').singular })
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData)

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return
    if (!hasChanges) {
      showSuccess(t('common.noChangesToSave'))
      return
    }
    try {
      setLoading(true)
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        client_id_input: parseInt(formData.client),
        date: formData.date,
      }
      const params = isWorkspaceSwitched && ownerAccountId ? { owner_account_id: ownerAccountId } : {}
      const response = await apiService.put(`/cases/${id}/`, payload, { params })
      if (response.success) {
        showSuccess(t('documents.updateSuccess', { entity: getEntity('case').singular }))
        navigate(`/cases/${id}`)
      } else if (response.error?.data) {
        setErrors(response.error.data)
      } else {
        showError(response.error?.message || t('documents.updateFailed', { entity: getEntity('case').singular.toLowerCase() }))
      }
    } catch {
      showError(t('documents.updateFailed', { entity: getEntity('case').singular.toLowerCase() }))
    } finally {
      setLoading(false)
    }
  }

  if (pageLoading) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-muted rounded-lg animate-pulse" />
          <div className="h-7 bg-muted rounded w-1/3 animate-pulse" />
        </div>
        <Card padding="md">
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </Card>
        <div className="flex justify-center"><Spinner /></div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <IconButton
          icon={HiOutlineArrowLeft}
          aria-label={t('cases.detail.backTo', { entity: getEntity('case').singular.toLowerCase() })}
          variant="ghost"
          onClick={() => navigate(`/cases/${id}`)}
          className="rtl:rotate-180"
        />
        <PageHeader
          icon={HiOutlinePencil}
          iconTone="info"
          title={t('cases.edit.title', { entity: getEntity('case').singular.toLowerCase() })}
          subtitle={t('cases.edit.subtitle', { entity: getEntity('case').singular.toLowerCase() })}
          className="!mb-0 flex-1"
        />
      </div>

      <Card padding="none">
        <Card.Header className="px-5 sm:px-6 py-4 mb-0 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <HiOutlineFolder className="w-4 h-4 text-ink-muted" />
            <h2 className="text-sm font-semibold text-ink-primary">
              {t('cases.create.sectionTitle', { entity: getEntity('case').singular })}
            </h2>
          </div>
        </Card.Header>

        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          <FormField label={t('cases.create.titleLabel', { entity: getEntity('case').singular })} required error={errors.title}>
            <Input
              name="title"
              size="lg"
              value={formData.title}
              onChange={handleChange}
              leadingIcon={HiOutlineFolder}
              placeholder={t('cases.create.titlePlaceholder', { entity: getEntity('case').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('cases.create.clientLabel', { client: getEntity('client').singular })} required error={errors.client}>
            <Select
              name="client"
              size="lg"
              value={formData.client}
              onChange={handleChange}
              disabled={loading || clientsLoading}
            >
              <option value="">
                {clientsLoading ? t('common.loading') : t('cases.create.clientPlaceholder', { client: getEntity('client').singular.toLowerCase() })}
              </option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </FormField>

          <FormField label={t('cases.create.dateLabel', { entity: getEntity('case').singular })} required error={errors.date}>
            <Input
              type="date"
              name="date"
              size="lg"
              value={formData.date}
              onChange={handleChange}
              leadingIcon={HiOutlineCalendarDays}
              disabled={loading}
            />
          </FormField>

          <FormField label={t('cases.create.descriptionLabel')} error={errors.description}>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder={t('cases.create.descriptionPlaceholder', { entity: getEntity('case').singular.toLowerCase() })}
              disabled={loading}
            />
          </FormField>

          {hasChanges && <Banner tone="info">{t('common.youHaveUnsavedChanges')}</Banner>}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-6 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={() => navigate(`/cases/${id}`)} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="lg"
              leadingIcon={loading ? null : HiOutlineCheckCircle}
              loading={loading}
              disabled={!hasChanges}
            >
              {loading ? t('common.saving') : t('common.saveChanges')}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
